/**
 * Utility functions for snapping doors, windows, and openings to walls
 */

import {
  calculateObjectPosition,
  initializeObject,
} from '../components/3d/utils/objectSnapping';

/**
 * Snap a door/window/opening position to the nearest wall
 * @param {Object} elementCenter - The element center with x and z coordinates
 * @param {Array} walls - Array of wall objects
 * @param {Object} elementDimensions - The element dimensions (x, y, z)
 * @param {string} elementType - Type of element ('door', 'window', 'opening')
 * @param {number} floorY - The Y position of the floor (optional)
 * @returns {Object|null} Snapped position and rotation, or null if no wall nearby
 */
export const snapElementToWall = (
  elementCenter,
  walls,
  elementDimensions,
  elementType,
  floorY = 0
) => {
  // Create an element object compatible with our unified utility
  const element = {
    type: elementType,
    center: {
      x: elementCenter.x,
      y: elementCenter.y || floorY,
      z: elementCenter.z,
    },
    dimensions: elementDimensions,
    rotation: { x: 0, y: 0, z: 0 },
  };

  // Initialize with alignment properties
  const initializedElement = initializeObject(element);

  // Use unified positioning utility
  const { position, rotation, snappedWall, snappedWallNormal } =
    calculateObjectPosition({
      object: initializedElement,
      newDimensions: elementDimensions,
      walls: walls,
      floorY: floorY,
      previousDimensions: elementDimensions,
    });

  // Check if position was calculated
  if (!position) {
    return null;
  }

  // Use the wall from calculateObjectPosition (same wall that determined placement)
  if (!snappedWall) {
    // For elements that require walls, reject if no wall was found
    if (initializedElement.wallAlignment !== 'none') {
      return null;
    }
  }

  // Compute wall direction from the snapped wall's start/end
  let wallDirection = null;
  if (snappedWall?.start && snappedWall?.end) {
    const dx = snappedWall.end.x - snappedWall.start.x;
    const dz = snappedWall.end.z - snappedWall.start.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len > 0) {
      wallDirection = { x: dx / len, z: dz / len };
    }
  }

  return {
    position: position,
    rotation: rotation,
    wall: snappedWall || null,
    wallDirection: wallDirection,
    wallNormal: snappedWallNormal || null,
  };
};

/**
 * Check if a position is on a wall
 * @param {Object} position - The position with x and z coordinates
 * @param {Object} wall - The wall object
 * @param {number} tolerance - Tolerance for distance check (in meters)
 * @returns {boolean} True if position is on the wall
 */
export const isPositionOnWall = (position, wall, tolerance = 0.1) => {
  if (!wall.start || !wall.end) return false;

  // Calculate wall vector
  const wallVector = {
    x: wall.end.x - wall.start.x,
    z: wall.end.z - wall.start.z,
  };

  // Calculate wall length
  const wallLength = Math.sqrt(
    wallVector.x * wallVector.x + wallVector.z * wallVector.z
  );

  // Normalize wall vector
  const wallDir = {
    x: wallVector.x / wallLength,
    z: wallVector.z / wallLength,
  };

  // Vector from wall start to position
  const toPoint = {
    x: position.x - wall.start.x,
    z: position.z - wall.start.z,
  };

  // Project position onto wall line
  const projection = toPoint.x * wallDir.x + toPoint.z * wallDir.z;

  // Check if projection is within wall bounds
  if (projection < -tolerance || projection > wallLength + tolerance) {
    return false;
  }

  // Calculate perpendicular distance to wall line
  const perpDistance = Math.abs(toPoint.x * -wallDir.z + toPoint.z * wallDir.x);

  // Check if perpendicular distance is within tolerance
  return perpDistance <= tolerance;
};

/**
 * Calculate the best position for an element on a wall
 * @param {Object} wall - The wall object
 * @param {Object} elementDimensions - The element dimensions
 * @param {number} preferredPosition - Preferred position along wall (0-1)
 * @returns {Object} The calculated position and rotation
 */
export const calculateElementPositionOnWall = (
  wall,
  elementDimensions,
  preferredPosition = 0.5
) => {
  if (!wall.start || !wall.end) {
    // Fallback to wall center if start/end not available
    return {
      position: wall.center || { x: 0, y: 0, z: 0 },
      rotation: wall.rotation || { x: 0, y: 0, z: 0 },
    };
  }

  // Calculate wall vector
  const wallVector = {
    x: wall.end.x - wall.start.x,
    z: wall.end.z - wall.start.z,
  };

  // Calculate position along wall
  const position = {
    x: wall.start.x + wallVector.x * preferredPosition,
    y: wall.center ? wall.center.y : 0,
    z: wall.start.z + wallVector.z * preferredPosition,
  };

  // Calculate rotation to align with wall
  // Negate Z to match the canvas coordinate system where Y increases downward
  const wallAngle = Math.atan2(-wallVector.z, wallVector.x);

  return {
    position: position,
    rotation: { x: 0, y: wallAngle, z: 0 },
  };
};
